import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { FilterList } from '@mui/icons-material';
import { salesLeadService } from '../services/salesLeadService';
import { employeeService } from '../services/employeeService';
import usePersistedListFilters from '../hooks/usePersistedListFilters';

const STATUS_COLORS = {
  new: 'info',
  contacted: 'primary',
  quoting: 'warning',
  converted: 'success',
  lost: 'default',
  dead: 'default',
};

const LIST_FILTERS_DEFAULTS = {
  search: '',
  status: '',
  repId: ''
};

const Leads = () => {
  const navigate = useNavigate();
  const {
    values: listFilters,
    setField: setListFilter,
    clear: clearListFilters,
    hasActiveFilters
  } = usePersistedListFilters('wm_leads_list_filters', LIST_FILTERS_DEFAULTS);
  const search = listFilters.search;
  const setSearch = useCallback((v) => setListFilter('search', v), [setListFilter]);
  const status = listFilters.status;
  const setStatus = useCallback((v) => setListFilter('status', v), [setListFilter]);
  const repId = listFilters.repId;
  const setRepId = useCallback((v) => setListFilter('repId', v), [setListFilter]);
  const [rows, setRows] = useState([]);
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await employeeService.getAll({ limit: 500 });
        const list = res.data || res || [];
        setReps(
          (Array.isArray(list) ? list : []).filter(
            (e) => e.role === 'sales_rep' || e.is_active !== false
          )
        );
      } catch {
        /* optional */
      }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await salesLeadService.getAll({
        search: search || undefined,
        status: status || undefined,
        sales_rep_employee_id: repId || undefined,
        limit: 200,
      });
      setRows(res.data || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [search, status, repId]);

  useEffect(() => {
    load();
  }, [load]);

  const repName = (row) =>
    `${row.sales_rep_first_name || ''} ${row.sales_rep_last_name || ''}`.trim() || '—';

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>Sales Leads</Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 220 }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {Object.keys(STATUS_COLORS).map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Sales Rep</InputLabel>
            <Select label="Sales Rep" value={repId} onChange={(e) => setRepId(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {reps
                .filter((e) => e.role === 'sales_rep')
                .map((e) => (
                  <MenuItem key={e.id} value={String(e.id)}>
                    {e.first_name} {e.last_name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          {hasActiveFilters ? (
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterList />}
              onClick={clearListFilters}
            >
              Clear filters
            </Button>
          ) : null}
          <Button variant="outlined" onClick={load}>Refresh</Button>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Lead #</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Sales Rep</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Converted Customer</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center">No leads</TableCell></TableRow>
              ) : rows.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  sx={{ cursor: row.converted_customer_id ? 'pointer' : 'default' }}
                  onClick={() => {
                    if (row.converted_customer_id) {
                      navigate(`/customers/${row.converted_customer_id}`);
                    }
                  }}
                >
                  <TableCell>{row.lead_number}</TableCell>
                  <TableCell>{row.company_name}</TableCell>
                  <TableCell>{row.contact_name || '—'}</TableCell>
                  <TableCell>{row.lead_source || '—'}</TableCell>
                  <TableCell>{repName(row)}</TableCell>
                  <TableCell>
                    <Chip size="small" label={row.status} color={STATUS_COLORS[row.status] || 'default'} />
                  </TableCell>
                  <TableCell>
                    {row.converted_customer_id
                      ? `#${row.converted_customer_number} ${row.converted_customer_name || ''}`
                      : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default Leads;
